import { Permission, userHasAnyPermission } from "~/domains/user/utils/permissions";
import { userIsLoggedIn, userIsRegistered } from "~/domains/user/utils/roles";
import { useSelector } from "react-redux";
import { selectWishlistAmount } from "~/features/wishlist/redux/wishlistSelectors";
import { REMOVE_SALES_PARTNER_FINDER } from "~/utils/constants";
import useMediaQuery from "~/common/hooks/useMediaQuery";
import { STORE_FINDER } from "~/utils/url/staticUrls";
import { getWishlistNavigationEntryUrl, useLoadWishlistsOnMount } from "~/features/wishlist/utils/wishlistHelper";
import { canSeeFCC, canSeeMyCompany } from "~/features/navigation/components/headerMenu/DesktopPersonalNav";
import { MEDIA_QUERY_MIN_TABLET } from "~/common/utils/screenSizes";
import { NotificationNavItem } from "~/features/navigation/components/Notifications/NotificationNavItem";
import { getProductComparisonNavigationEntryUrl } from "~/features/compareProducts/utils/productComparisonHelper";
import { selectProductComparisonList } from "~/features/compareProducts/redux/productComparisonSelectors";
import { useTranslation } from "react-i18next";
import { canSeeWishlistButton } from "~/utils/features";
import { HeaderMinicart } from "~/features/cart/minicart/MinicartComponent";
import { DesktopScanDMCTeaser } from "~/features/navigation/components/DesktopScanDMCTeaser/DesktopScanDMCTeaser";
import { NavItems } from "~/features/navigation/components/headerMenu/PersonalNavItems";
import { PersonalNavItem } from "~/features/navigation/components/headerMenu/PersonalNavItem";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import CompareOutlinedIcon from "@mui/icons-material/CompareOutlined";
import { styled } from "~/common/mui";

const WISHLIST_URL = getWishlistNavigationEntryUrl();
const PRODUCT_COMPARISON_URL = getProductComparisonNavigationEntryUrl();

export const canSeeSalesFinder = () => !REMOVE_SALES_PARTNER_FINDER && (!userIsLoggedIn() || userIsRegistered());

export const MobilePersonalNav = () => {
  const { t } = useTranslation();
  const canSeeCart = userHasAnyPermission(Permission.ADD_TO_CART, Permission.ADD_TO_QUOTE);

  useLoadWishlistsOnMount();

  const isMinTablet = useMediaQuery(MEDIA_QUERY_MIN_TABLET);

  const wishlistAmount = useSelector(selectWishlistAmount);

  const listItems = useSelector(selectProductComparisonList).length;

  return (
    <PersonalNavRoot className="scha__personal-nav">
      <PersonalNavList className="scha__personal-nav__list">
        {isMinTablet && canSeeFCC() && (
          <li className="scha__personal-nav__list__item">
            <NavItems.FCC />
          </li>
        )}
        {isMinTablet && !canSeeFCC() && (
          <li className="scha__personal-nav__list__item">
            <DesktopScanDMCTeaser />
          </li>
        )}
        {canSeeMyCompany() && (
          <li className="scha__personal-nav__list__item">
            <NavItems.MyCompany />
          </li>
        )}
        {isMinTablet && canSeeSalesFinder() && (
          <li className="scha__personal-nav__list__item">
            <a href={STORE_FINDER} data-test-id={"header-sales-partner"}>
              <PersonalNavItem
                data-test-id={"header-btn-sales-partner"}
                icon={<LocationOnOutlinedIcon />}
                name={t("salesPartner.title")}
              />
            </a>
          </li>
        )}
        <NotificationNavItem />
        <li className="scha__personal-nav__list__item">
          <a href={PRODUCT_COMPARISON_URL} id="home_comparison_list_btn" data-test-id={"header-wishlist"}>
            <PersonalNavItem
              icon={<CompareOutlinedIcon />}
              badgeContent={listItems}
              name={t("header.link.comparison.list")}
            />
          </a>
        </li>
        {isMinTablet && canSeeWishlistButton() && (
          <li className="scha__personal-nav__list__item">
            <a href={WISHLIST_URL} id="home_wishlist_btn" data-test-id={"header-wishlist"}>
              <PersonalNavItem
                name={t("wishlistPage.title")}
                icon={<BookmarkBorderOutlinedIcon />}
                data-test-id={"header-btn-wishlist"}
                badgeContent={wishlistAmount !== undefined ? wishlistAmount : ""}
              />
            </a>
          </li>
        )}
        {canSeeCart && (
          <li className="scha__personal-nav__list__item">
            <HeaderMinicart />
          </li>
        )}
      </PersonalNavList>
    </PersonalNavRoot>
  );
};

const PersonalNavRoot = styled("div")({
  height: "100%",
  width: "100%",
});

const PersonalNavList = styled("ul")(({ theme }) => ({
  display: "flex",
  height: "inherit",
  justifyContent: "flex-end",
  gap: theme.spacing(0.5),
  paddingLeft: 0,
  "& > .scha__personal-nav__list__item": {
    display: "flex",
    alignItems: "center",
    listStyle: "none",
  },
}));
